import { useContext, createContext, useState, useCallback, useMemo, useEffect } from 'react'
import useNavigate from '../component/hooks/useNavigate'
import { useSessionStorage } from 'usehooks-ts'

const emptyToken = { accessToken: '', refreshToken: '' }

const AuthContext = createContext({
  token: emptyToken,
  user: null,
  setUser: () => {},
  login: () => Promise.resolve(),
  logOut: () => {}
})

const AuthProvider = ({ children }) => {
  const [user, setUser] = useSessionStorage('user', null)

  const [token, setToken] = useState(() => ({
    accessToken: sessionStorage.getItem('accessToken') || '',
    refreshToken: sessionStorage.getItem('refreshToken') || ''
  }))

  const navigate = useNavigate()

  useEffect(() => {
    if (token.accessToken && token.refreshToken) {
      sessionStorage.setItem('accessToken', token.accessToken)
      sessionStorage.setItem('refreshToken', token.refreshToken)
    } else {
      sessionStorage.removeItem('accessToken')
      sessionStorage.removeItem('refreshToken')
    }
  }, [token])

  const login = useCallback(
    (res) => {
      setUser(res.data)
      setToken({
        accessToken: res.accessToken,
        refreshToken: res.refreshToken
      })
      navigate('/')
    },
    [navigate, setUser]
  )

  const logOut = useCallback(() => {
    setUser(null)
    setToken(emptyToken)
    navigate('/login')
  }, [navigate, setUser])

  const contextValue = useMemo(
    () => ({
      token,
      user,
      setUser,
      login,
      logOut
    }),
    [token, user, setUser, login, logOut]
  )

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export default AuthProvider
export const useAuth = () => useContext(AuthContext)
